import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { blogService } from '@/services/blogService';
import { BlogProse } from '@/components/blog/BlogProse';
import { BlogSidebar } from '@/components/blog/BlogSidebar';
import { BlogCard } from '@/components/blog/BlogCard';
import { ShareButtons } from '@/components/blog/ShareButtons';
import { PublicHeader } from '@/components/Layout/PublicHeader';
import { PublicFooter } from '@/components/Layout/PublicFooter';
import type { BlogPost, BlogCategory, PostNavigation } from '@/types/blog';

export function BlogDetail() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [navigation, setNavigation] = useState<PostNavigation>({ previous: null, next: null });
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [recentPosts, setRecentPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    const loadData = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const [postData, navData, categoriesData, recentData] = await Promise.all([
          blogService.getPublicPost(slug),
          blogService.getPostNavigation(slug),
          blogService.listPublicCategories(),
          blogService.getRecentPosts(5),
        ]);
        setPost(postData);
        setNavigation(navData);
        setCategories(categoriesData);
        setRecentPosts(recentData);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [slug]);

  useEffect(() => {
    if (post) {
      document.title = `${post.meta_title || post.title} | Cascady`;
    }
    return () => { document.title = 'Cascady'; };
  }, [post]);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <PublicHeader />
        <main className="flex-1">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="animate-pulse space-y-6 max-w-3xl">
              <div className="h-8 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-1/3" />
              <div className="aspect-[2/1] bg-gray-200 rounded-xl" />
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded w-2/3" />
              </div>
            </div>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <PublicHeader />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Post Not Found</h1>
            <p className="text-gray-600 mb-6">The post you're looking for doesn't exist or has been removed.</p>
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors"
            >
              Back to Blog
            </Link>
          </div>
        </main>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <PublicHeader />

      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-10">
            {/* Article */}
            <article>
              {/* Back link */}
              <div className="mb-6">
                <Link
                  to="/blog"
                  className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back to Blog
                </Link>
              </div>

              {/* Category */}
              {post.category_name && (
                <Link
                  to={`/blog?category=${post.category_slug}`}
                  className="inline-block text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-3 hover:text-emerald-700"
                >
                  {post.category_name}
                </Link>
              )}

              {/* Title */}
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                {post.title}
              </h1>

              {/* Meta */}
              <div className="flex items-center gap-3 text-sm text-gray-500 mb-8 pb-8 border-b border-gray-200">
                {post.author_name && (
                  <span className="font-medium text-gray-700">{post.author_name}</span>
                )}
                {post.published_at && (
                  <>
                    {post.author_name && <span>·</span>}
                    <span>{formatDate(post.published_at)}</span>
                  </>
                )}
                {post.reading_time && (
                  <>
                    <span>·</span>
                    <span>{post.reading_time} min read</span>
                  </>
                )}
              </div>

              {/* Featured Image */}
              {post.featured_image && (
                <div className="mb-8 rounded-xl overflow-hidden">
                  <img
                    src={post.featured_image}
                    alt={post.title}
                    className="w-full h-auto object-cover"
                  />
                </div>
              )}

              {/* Content */}
              <BlogProse html={post.content} className="mb-10" />

              {/* Share */}
              <div className="pt-8 border-t border-gray-200 mb-10">
                <ShareButtons url={currentUrl} title={post.title} />
              </div>

              {/* Previous/Next */}
              {(navigation.previous || navigation.next) && (
                <div className="flex items-stretch gap-4 pt-8 border-t border-gray-200">
                  {navigation.previous ? (
                    <Link
                      to={`/blog/${navigation.previous.slug}`}
                      className="flex-1 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors group"
                    >
                      <p className="text-xs text-gray-500 mb-1">Previous</p>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-2">
                        {navigation.previous.title}
                      </p>
                    </Link>
                  ) : <div className="flex-1" />}
                  {navigation.next ? (
                    <Link
                      to={`/blog/${navigation.next.slug}`}
                      className="flex-1 p-4 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors group text-right"
                    >
                      <p className="text-xs text-gray-500 mb-1">Next</p>
                      <p className="text-sm font-medium text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-2">
                        {navigation.next.title}
                      </p>
                    </Link>
                  ) : <div className="flex-1" />}
                </div>
              )}
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block">
              <BlogSidebar
                categories={categories}
                recentPosts={recentPosts}
              />
            </aside>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
}
